/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { FunctionComponent, LegacyRef, ReactInstance, ErrorInfo, Key } from 'react';
import type { Observable } from 'rxjs/Observable';
import { fireOperationalAnalytics } from '@jira/platform__analytics-web-react/src/utils/fire-operational-event.tsx';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import withInjectedProps from '@atlassian/jira-common-util-with-injected-props/src/index.tsx';
import type { LegacyBreadcrumbItem, LegacyBreadcrumbId } from '../types.tsx';
import LegacyApiBridge from './component.tsx';
import type { AnalyticsEventProps, AnalyticsEvents, Props } from './types.tsx';

const analyticsEvents: AnalyticsEvents = {
	onInsertItemFired: (analyticsEvent) =>
		fireOperationalAnalytics(analyticsEvent, {
			attributes: { method: 'insertItem' },
		}),
	onRemoveItemFired: (analyticsEvent) =>
		fireOperationalAnalytics(analyticsEvent, {
			attributes: { method: 'removeItem' },
		}),
	onSetRenderTargetFired: (analyticsEvent) =>
		fireOperationalAnalytics(analyticsEvent, {
			attributes: { method: 'render' },
		}),
	onOnBreadCrumbsUpdateFired: (analyticsEvent) =>
		fireOperationalAnalytics(analyticsEvent, {
			attributes: { method: 'onBreadCrumbsUpdate' },
		}),
};

const LegacyAPIBridgeConnected: FunctionComponent<
	Omit<
		Omit<
			Omit<
				{
					onInsertItem: (arg1: LegacyBreadcrumbItem) => void;
					onRemoveItem: (arg1: LegacyBreadcrumbId) => void;
					onSetRenderTarget: (arg1: HTMLElement) => void;
					onInsertItemFired: (args_0: UIAnalyticsEvent) => void;
					onRemoveItemFired: (args_0: UIAnalyticsEvent) => void;
					onSetRenderTargetFired: (args_0: UIAnalyticsEvent) => void;
					onOnBreadCrumbsUpdateFired: (args_0: UIAnalyticsEvent) => void;
					ref?:
						| LegacyRef<{
								componentDidMount(): void;
								onInsertItem: (item: LegacyBreadcrumbItem) => void;
								onRemoveItem: (itemId: LegacyBreadcrumbId) => void;
								onSetRenderTarget: (element: HTMLElement) => void;
								onBreadCrumbsUpdate: () => Observable<never>;
								render(): null;
								context: unknown;
								setState<K extends never>(
									state:
										| {}
										| ((
												prevState: Readonly<{}>,
												props: Readonly<Props & AnalyticsEventProps>,
										  ) => {} | Pick<{}, K> | null)
										| Pick<{}, K>
										| null,
									callback?: (() => void) | undefined,
								): void;
								forceUpdate(callback?: (() => void) | undefined): void;
								readonly props: Readonly<Props & AnalyticsEventProps>;
								state: Readonly<{}>;
								refs: {
									[key: string]: ReactInstance;
								};

								shouldComponentUpdate?(
									nextProps: Readonly<Props & AnalyticsEventProps>,
									nextState: Readonly<{}>,
									// oxlint-disable-next-line @typescript-eslint/no-explicit-any
									nextContext: any,
								): boolean;
								componentWillUnmount?(): void;
								componentDidCatch?(error: Error, errorInfo: ErrorInfo): void;

								getSnapshotBeforeUpdate?(
									prevProps: Readonly<Props & AnalyticsEventProps>,
									prevState: Readonly<{}>,
									// oxlint-disable-next-line @typescript-eslint/no-explicit-any
								): any;

								componentDidUpdate?(
									prevProps: Readonly<Props & AnalyticsEventProps>,
									prevState: Readonly<{}>,
									// oxlint-disable-next-line @typescript-eslint/no-explicit-any
									snapshot?: any,
								): void;
								componentWillMount?(): void;
								UNSAFE_componentWillMount?(): void;

								componentWillReceiveProps?(
									nextProps: Readonly<Props & AnalyticsEventProps>,
									// oxlint-disable-next-line @typescript-eslint/no-explicit-any
									nextContext: any,
								): void;

								UNSAFE_componentWillReceiveProps?(
									nextProps: Readonly<Props & AnalyticsEventProps>,
									// oxlint-disable-next-line @typescript-eslint/no-explicit-any
									nextContext: any,
								): void;

								componentWillUpdate?(
									nextProps: Readonly<Props & AnalyticsEventProps>,
									nextState: Readonly<{}>,
									// oxlint-disable-next-line @typescript-eslint/no-explicit-any
									nextContext: any,
								): void;

								UNSAFE_componentWillUpdate?(
									nextProps: Readonly<Props & AnalyticsEventProps>,
									nextState: Readonly<{}>,
									// oxlint-disable-next-line @typescript-eslint/no-explicit-any
									nextContext: any,
								): void;
						  }>
						| undefined;
					key?: Key | null | undefined;
				},
				keyof WithAnalyticsEventsProps
			>,
			keyof Props | keyof AnalyticsEventProps | 'key'
		> &
			Partial<
				Partial<
					Omit<
						{
							onInsertItem: (arg1: LegacyBreadcrumbItem) => void;
							onRemoveItem: (arg1: LegacyBreadcrumbId) => void;
							onSetRenderTarget: (arg1: HTMLElement) => void;
							onInsertItemFired: (args_0: UIAnalyticsEvent) => void;
							onRemoveItemFired: (args_0: UIAnalyticsEvent) => void;
							onSetRenderTargetFired: (args_0: UIAnalyticsEvent) => void;
							onOnBreadCrumbsUpdateFired: (args_0: UIAnalyticsEvent) => void;
							ref?:
								| LegacyRef<{
										componentDidMount(): void;
										onInsertItem: (item: LegacyBreadcrumbItem) => void;
										onRemoveItem: (itemId: LegacyBreadcrumbId) => void;
										onSetRenderTarget: (element: HTMLElement) => void;
										onBreadCrumbsUpdate: () => Observable<never>;
										render(): null;
										context: unknown;
										setState<K extends never>(
											state:
												| {}
												| ((
														prevState: Readonly<{}>,
														props: Readonly<Props & AnalyticsEventProps>,
												  ) => {} | Pick<{}, K> | null)
												| Pick<{}, K>
												| null,
											callback?: (() => void) | undefined,
										): void;
										forceUpdate(callback?: (() => void) | undefined): void;
										readonly props: Readonly<Props & AnalyticsEventProps>;
										state: Readonly<{}>;
										refs: {
											[key: string]: ReactInstance;
										};

										shouldComponentUpdate?(
											nextProps: Readonly<Props & AnalyticsEventProps>,
											nextState: Readonly<{}>,
											// oxlint-disable-next-line @typescript-eslint/no-explicit-any
											nextContext: any,
										): boolean;
										componentWillUnmount?(): void;
										componentDidCatch?(error: Error, errorInfo: ErrorInfo): void;

										getSnapshotBeforeUpdate?(
											prevProps: Readonly<Props & AnalyticsEventProps>,
											prevState: Readonly<{}>,
											// oxlint-disable-next-line @typescript-eslint/no-explicit-any
										): any;

										componentDidUpdate?(
											prevProps: Readonly<Props & AnalyticsEventProps>,
											prevState: Readonly<{}>,
											// oxlint-disable-next-line @typescript-eslint/no-explicit-any
											snapshot?: any,
										): void;
										componentWillMount?(): void;
										UNSAFE_componentWillMount?(): void;

										componentWillReceiveProps?(
											nextProps: Readonly<Props & AnalyticsEventProps>,
											// oxlint-disable-next-line @typescript-eslint/no-explicit-any
											nextContext: any,
										): void;

										UNSAFE_componentWillReceiveProps?(
											nextProps: Readonly<Props & AnalyticsEventProps>,
											// oxlint-disable-next-line @typescript-eslint/no-explicit-any
											nextContext: any,
										): void;

										componentWillUpdate?(
											nextProps: Readonly<Props & AnalyticsEventProps>,
											nextState: Readonly<{}>,
											// oxlint-disable-next-line @typescript-eslint/no-explicit-any
											nextContext: any,
										): void;

										UNSAFE_componentWillUpdate?(
											nextProps: Readonly<Props & AnalyticsEventProps>,
											nextState: Readonly<{}>,
											// oxlint-disable-next-line @typescript-eslint/no-explicit-any
											nextContext: any,
										): void;
								  }>
								| undefined;
							key?: Key | null | undefined;
						},
						keyof WithAnalyticsEventsProps
					>
				>
			>,
		keyof AnalyticsEvents
	>
> = withInjectedProps(LegacyApiBridge, analyticsEvents);
export default LegacyAPIBridgeConnected;
